import { useState } from 'react';
import { Mail, Newspaper, Users, Globe, Sparkles } from 'lucide-react';
import { ServiceCard } from './ServiceCard';

// Services data configuration
const SERVICES_DATA = [
  {
    id: 'email',
    icon: Mail,
    title: 'Email Marketing',
    description: 'Campaigns, flows, retention strategy.',
    image: 'https://images.unsplash.com/photo-1763889831889-3f735612ce34?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxjcmVhdGl2ZSUyMHdvcmtzcGFjZSUyMGxhcHRvcCUyMGNvZmZlZSUyMG1pbmltYWx8ZW58MHx8fHwxNzcwNzcyNDE0fDA&ixlib=rb-4.1.0&q=85',
    stat: '+127%',
    statLabel: 'Revenue',
    details: {
      intro: 'Turn your email list into a revenue machine with strategic campaigns that convert.',
      includes: [
        'Welcome sequence setup',
        'Abandoned cart flows',
        'Re-engagement campaigns',
        'Monthly newsletter strategy',
        'A/B testing & optimization',
        'Performance analytics'
      ],
      cta: 'Book Email Strategy Call'
    }
  },
  {
    id: 'newsletters',
    icon: Newspaper,
    title: 'Newsletters',
    description: 'Consistent content that builds trust.',
    image: 'https://images.unsplash.com/photo-1752650735496-7cf951aef58f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjB0eXBpbmclMjBsYXB0b3AlMjBjcmVhdGl2ZSUyMHdvcmt8ZW58MHx8fHwxNzcwNzcyMzkzfDA&ixlib=rb-4.1.0&q=85',
    stat: '48%',
    statLabel: 'Open Rate',
    details: {
      intro: 'Build an audience that actually reads and engages with your content every week.',
      includes: [
        'Content strategy & calendar',
        'Newsletter copywriting',
        'Subject line optimization',
        'Audience segmentation',
        'Growth tactics & lead magnets',
        'Engagement tracking'
      ],
      cta: 'Book Newsletter Call'
    }
  },
  {
    id: 'social',
    icon: Users,
    title: 'Social & Community',
    description: 'Content, engagement, growth.',
    image: 'https://images.unsplash.com/photo-1758599543129-bec77e427ac6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNhbGwlMjBwaG9uZSUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzA3NzIzOTh8MA&ixlib=rb-4.1.0&q=85',
    stat: '3x',
    statLabel: 'Engagement',
    details: {
      intro: 'Grow your presence and build a community that advocates for your brand.',
      includes: [
        'Content strategy & planning',
        'Post copywriting & scheduling',
        'Community management',
        'Engagement & DM responses',
        'Influencer outreach',
        'Monthly performance reports'
      ],
      cta: 'Book Social Strategy Call'
    }
  },
  {
    id: 'websites',
    icon: Globe,
    title: 'Websites',
    description: 'Clean, conversion-focused builds.',
    image: 'https://images.unsplash.com/photo-1760278041881-e64e501d009c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxjcmVhdGl2ZSUyMHdvcmtzcGFjZSUyMGxhcHRvcCUyMGNvZmZlZSUyMG1pbmltYWx8ZW58MHx8fHwxNzcwNzcyNDE0fDA&ixlib=rb-4.1.0&q=85',
    stat: 'SEO',
    statLabel: 'Targeted',
    details: {
      intro: 'Get a website that looks great and actually converts visitors into customers.',
      includes: [
        'Custom design & development',
        'Mobile-first responsive build',
        'SEO optimization',
        'Speed & performance tuning',
        'Analytics setup',
        'Ongoing support & updates'
      ],
      cta: 'Book Website Call'
    }
  }
];

/**
 * ServicesHeader - Section header with title and description
 */
const ServicesHeader = () => (
  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-12">
    <div>
      <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#00D4FF]/10 text-[#00D4FF] text-sm font-medium mb-4 border border-[#00D4FF]/20">
        <Sparkles size={14} />
        Services
      </span>
      <h2
        data-testid="services-headline"
        className="text-3xl md:text-4xl font-bold tracking-tight text-white"
        style={{ fontFamily: 'Manrope, sans-serif' }}
      >
        What I <span className="text-gradient">deliver</span>
      </h2>
    </div>
    <p className="text-sm text-gray-400 max-w-xs">
      Click each service to see what&apos;s included.
    </p>
  </div>
);

/**
 * ServicesBackground - Animated background elements
 */
const ServicesBackground = () => (
  <>
    <div className="absolute inset-0 dot-pattern opacity-30" />
    <div className="absolute top-20 right-20 w-64 h-64 bg-[#00D4FF]/10 rounded-full blur-3xl animate-blob" />
    <div className="absolute bottom-20 left-20 w-48 h-48 bg-[#0EA5E9]/10 rounded-full blur-3xl animate-blob animation-delay-2000" />
    <Sparkles className="absolute top-32 left-[10%] text-[#00D4FF]/30 animate-float" size={24} />
    <Sparkles className="absolute bottom-40 right-[15%] text-[#00D4FF]/20 animate-float animation-delay-1000" size={20} />
  </>
);

/**
 * Services - Main services section component
 */
export const Services = () => {
  const [expandedService, setExpandedService] = useState(null);

  const toggleService = (serviceId) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-20 md:py-28 bg-[#1E1E2E] relative overflow-hidden"
    >
      <ServicesBackground />
      
      <div className="max-w-6xl mx-auto px-5 md:px-8 relative z-10">
        <ServicesHeader />

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {SERVICES_DATA.map((service) => (
            <ServiceCard
              key={service.id}
              service={service}
              isExpanded={expandedService === service.id}
              onToggle={() => toggleService(service.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
